import { PageOrderEnum } from "@/lib/enum/pageOrder.enum";
import { formatHoursAndMinutes, formatYearsMonthDay } from "@/lib/utility/date-helper";
import ticketService from "@/services/mrfiktiv/services/ticketService";
import {
  MrfiktivCreateTicketDtoGen,
  MrfiktivCustomFieldValueGen,
  MrfiktivPageViewModelGen,
  MrfiktivReferenceGen,
  MrfiktivShortUserViewModelGen,
  MrfiktivTicketControllerGetParamsGen,
  MrfiktivTicketViewModelGen,
  MrfiktivTimestampGen,
  MrfiktivUpdateTicketDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import Vue from "vue";
import { Action, getModule, Module, Mutation } from "vuex-module-decorators";
import { BasePagination, FilterTypes, IPaginationFilterListElement } from "./base-pagination.store";
import { FleetAggregationModule } from "./fleet-aggregation.store";
import { PartnerUserModule } from "./partner-user.store";
import { ProjectModule } from "./project.store";
import { VehicleModule } from "./vehicle.store";

export class Ticket
  implements Partial<MrfiktivTicketViewModelGen>, MrfiktivCreateTicketDtoGen, MrfiktivUpdateTicketDtoGen {
  /** identifier of ticket */
  id = "";

  /** identifier of partner that manages ticket */
  partnerId = "";

  /** identifier of user that created ticket */
  userId = "";

  /** The user */
  user?: MrfiktivShortUserViewModelGen;

  /** timestamp of the ticket */
  timestamp: MrfiktivTimestampGen = { created: "", lastModified: "" };

  /** unique number of the ticket */
  number = -1;

  /** project id that the ticket is assigned to */
  projectId?: string;

  /** the title of the ticket */
  title = "";

  /** the body of the ticket */
  body = "";

  /** tags for the ticket */
  tags?: string[];

  /** assignees id that are assigned to the ticket */
  assignees?: string[];

  /** The users assigned to the ticket */
  assigneesDetails?: MrfiktivShortUserViewModelGen[];

  /** Status of the ticket */
  state?: TicketStatusEnum;

  /**
   * when the ticket was closed
   * @format date-time
   */
  closedAt?: string;

  /** user id that closed the ticket */
  closedBy?: string;

  /** user details that closed the ticket */
  closedByDetails?: MrfiktivShortUserViewModelGen;

  /** values of the custom fields */
  values: MrfiktivCustomFieldValueGen[] = [];

  /** The references of the ticket */
  refs?: MrfiktivReferenceGen[];

  dueDate?: string;
  dueTime?: string = "00:00";

  loading = false;

  constructor(ticket?: MrfiktivTicketViewModelGen | MrfiktivCreateTicketDtoGen | MrfiktivUpdateTicketDtoGen) {
    Object.assign(this, ticket);

    this.refs = this.refs?.map(ref => {
      // quickfix cause frontend returns _id
      return { refId: ref.refId, refType: ref.refType };
    });

    if (!this.assignees) this.assignees = [];
  }

  get due(): string | undefined {
    if (this.dueDate) {
      const d = new Date(this.dueDate);

      // If no due time set it to "00:00" local time
      if (!this.dueTime) {
        this.dueTime = "00:00";
      }
      const [hours, minutes] = this.dueTime.split(":");
      d.setUTCHours(+hours, +minutes);
      return d.toISOString();
    }

    return undefined;
  }

  set due(due: string | undefined) {
    if (!due) {
      return;
    }

    const d = new Date(due);
    this.dueDate = formatYearsMonthDay(d);
    this.dueTime = formatHoursAndMinutes(d);
  }

  get dueDateTime() {
    if (this.dueDate) {
      return new Date(this.dueDate).getTime();
    }

    return undefined;
  }

  get createDto(): MrfiktivCreateTicketDtoGen {
    const dto: MrfiktivCreateTicketDtoGen = {
      body: this.body,
      projectId: this.projectId || undefined,
      tags: this.tags,
      title: this.title,
      values: this.values,
      assignees: this.assignees,
      refs: this.refs,
      state: this.state
    };

    if (this.due) {
      dto.due = this.due;
    }

    return dto;
  }

  get baseUpdateDto() {
    const dto: MrfiktivUpdateTicketDtoGen = {
      projectId: this.projectId,
      tags: this.tags,
      assignees: this.assignees
    };

    if (this.due) {
      dto.due = this.due;
    } else {
      dto.due = "";
    }

    return dto;
  }

  get updateDto(): MrfiktivUpdateTicketDtoGen {
    const dto: MrfiktivUpdateTicketDtoGen = {
      body: this.body,
      projectId: this.projectId,
      tags: this.tags,
      title: this.title,
      values: this.values.map(v => {
        return { id: v.id, value: v.value };
      }),
      assignees: this.assignees,
      refs: this.refs,
      state: this.state
    };

    if (this.due) {
      dto.due = this.due;
    }

    return dto;
  }

  get stateColor() {
    return TicketModule.ticketStateMap.get(this.state || TicketStatusEnum.DRAFT);
  }

  /**
   * Indicates if the given ticket has a due date
   */
  get isDue() {
    return this.due && this.state === TicketStatusEnum.OPEN;
  }

  /**
   * Indicates if the given ticket is overdue
   */
  get isOverdue() {
    if (this.due && this.state === TicketStatusEnum.OPEN) {
      return new Date(this.due) < new Date();
    }

    return false;
  }
}

/**
 * Default states of a ticket
 * TicketStateEnum
 */
export enum TicketStatusEnum {
  /**
   * Ticket is in a draft state
   */
  DRAFT = "draft",

  /**
   * Ticket is open
   */
  OPEN = "open",
  /**
   * Ticket is closed
   */
  CLOSED = "closed",

  /**
   * Ticket was archived
   */
  ARCHIVED = "archived"
}

export enum BooleanEnum {
  TRUE = "true",
  FALSE = "false"
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "ticket",
  store
})
export class TicketStore extends BasePagination<MrfiktivTicketViewModelGen, MrfiktivTicketControllerGetParamsGen> {
  protected _pageOrder: PageOrderEnum = PageOrderEnum.DESCENDING;
  protected _itemsPerPage = 100;
  protected _totalPages = 0;
  protected _paginationList: MrfiktivTicketViewModelGen[] = [];
  protected _currentPage = 1;
  protected _totalItems = 0;
  protected _isLoadAll = true;
  filterOptions: IPaginationFilterListElement[] = [...this.originalFilterList];

  get originalFilterList(): IPaginationFilterListElement[] {
    const mapItemToComponent = (item: any) => {
      return { item };
    };

    return [
      {
        key: "userId",
        displayName: "objects.ticket.userId",
        type: FilterTypes.OBJECT_ID,
        config: {
          items: this.userChoices,
          mapItemToComponent,
          itemValue: "id",
          component: "refs-user"
        }
      },
      {
        key: "projectId",
        displayName: "objects.ticket.projectId",
        type: FilterTypes.OBJECT_ID,
        config: {
          items: ProjectModule.paginationList,
          mapItemToComponent,
          itemValue: "id",
          component: "refs-project"
        }
      },
      {
        key: "state",
        displayName: "objects.ticket.state",
        type: FilterTypes.ENUM,
        config: { items: Object.values(TicketStatusEnum) }
      },
      {
        key: "assignees",
        displayName: "objects.ticket.assignees",
        type: FilterTypes.OBJECT_ID,
        config: {
          items: this.userChoices,
          mapItemToComponent,
          itemValue: "id",
          component: "refs-user"
        }
      },
      { key: "due", displayName: "objects.ticket.due", type: FilterTypes.DATE },
      { key: "tags", displayName: "objects.ticket.tags", type: FilterTypes.STRING },
      { key: "closedAt", displayName: "objects.ticket.closedAt", type: FilterTypes.DATE },
      {
        key: "closedBy",
        displayName: "objects.ticket.closedBy",
        type: FilterTypes.OBJECT_ID,
        config: {
          items: this.userChoices,
          mapItemToComponent,
          itemValue: "id",
          component: "refs-user"
        }
      },
      {
        key: "refs.refId",
        displayName: "common.nouns.vehicle",
        type: FilterTypes.OBJECT_ID,
        config: { items: VehicleModule?.paginationList, mapItemToComponent, itemValue: "id", component: "refs-vehicle" }
      }
    ];
  }

  private get userChoices() {
    return PartnerUserModule.paginationList;
  }

  private _ticketIdMap = new Map<string, MrfiktivTicketViewModelGen>();
  private _ticketNumberMap = new Map<string, MrfiktivTicketViewModelGen>();
  protected _documentMapConfig = { id: this._ticketIdMap, number: this._ticketNumberMap };

  get ticketStateMap() {
    return new Map<TicketStatusEnum, { color: string; label: string }>([
      [TicketStatusEnum.DRAFT, { color: "blue-grey lighten-4", label: "Draft" }],
      [TicketStatusEnum.OPEN, { color: "green lighten-1", label: "Open" }],
      [TicketStatusEnum.ARCHIVED, { color: "deep-purple lighten-4", label: "Archived" }],
      [TicketStatusEnum.CLOSED, { color: "deep-purple accent-2", label: "Closed" }]
    ]);
  }

  private _ticket: MrfiktivTicketViewModelGen | undefined = undefined;

  get tickets() {
    return this._paginationList;
  }

  private _isGetMine = false;

  get isGetMine() {
    return this._isGetMine;
  }

  @Mutation
  private _mutateIsGetMine(isGetMine: boolean) {
    this._isGetMine = isGetMine;
  }

  @Mutation
  private _mutateTicket(request: MrfiktivTicketViewModelGen) {
    this._ticket = request;
  }

  @Action
  setIsGetMine(isGetMine: boolean) {
    this.context.commit("_mutateIsGetMine", isGetMine);
  }

  @Action
  async loadDocuments(query: MrfiktivTicketControllerGetParamsGen) {
    let res: MrfiktivPageViewModelGen & {
      data?: MrfiktivTicketViewModelGen[] | undefined;
    };

    if (this.isGetMine) {
      const data = { ...query, partnerId: undefined };
      res = await ticketService.getMine(data);
    } else {
      res = await ticketService.getAll({ ...query });
    }

    Vue.$log.debug("Paged reports. Parsing for fleet ", res.data?.length);
    FleetAggregationModule.parseTickets(res.data ?? []);

    return res;
  }

  @Action
  async create(data: { partnerId: string; data: MrfiktivCreateTicketDtoGen }) {
    const res = await ticketService.create(data.partnerId, data.data);
    this.context.commit("_mutateTicket", res);
    this.addToList(res);
    FleetAggregationModule.parseTickets([res]);

    return res;
  }

  @Action
  async delete(data: { partnerId: string; ticketId: string }) {
    const res = await ticketService.delete(data.partnerId, data.ticketId);
    this.context.commit("_mutateTicket", res);

    this.removeInList(res);
    FleetAggregationModule.removeTicket(res.number);

    return res;
  }

  @Action
  async getOne(data: { partnerId: string; ticketId: string }) {
    const res = await ticketService.getOne(data.partnerId, data.ticketId);

    this.context.commit("_mutateTicket", res);
    FleetAggregationModule.replaceTicket(res);

    return res;
  }

  @Action
  async getOneByNumber(data: { partnerId: string; ticketNumber: number }) {
    const res = await ticketService.getOneByNumber(data.partnerId, data.ticketNumber);

    FleetAggregationModule.replaceTicket(res);
    this.context.commit("_mutateTicket", res);

    return res;
  }

  @Action
  async update(data: { partnerId: string; ticketId: string; data: MrfiktivUpdateTicketDtoGen }) {
    const res = await ticketService.update(data.partnerId, data.ticketId, data.data);

    this.context.commit("_mutateTicket", res);
    this.replaceInList(res);
    FleetAggregationModule.replaceTicket(res);

    return res;
  }

  @Action
  async getAll(data: { partnerId: string }) {
    await this.fetchAllFromBeginning({
      partnerId: data.partnerId
    });
  }

  @Action
  async archiveMany(data: { partnerId: string; tickets: MrfiktivTicketViewModelGen[] }) {
    const updated = await ticketService.archiveMany(
      data.partnerId,
      data.tickets.map(t => t.id)
    );
    for (const ticket of updated) {
      FleetAggregationModule.replaceTicket(ticket);
    }
  }

  @Action
  async deleteMany(data: { partnerId: string; tickets: MrfiktivTicketViewModelGen[] }) {
    await ticketService.deleteMany(
      data.partnerId,
      data.tickets.map(t => t.id)
    );
    for (const ticket of data.tickets) {
      FleetAggregationModule.removeTicket(ticket.number);
    }
  }
}

export const TicketModule = getModule(TicketStore);
